import { Fragment, type ReactNode } from 'react';

import { cn } from '../cn';

export type TouchpointSwimlaneSend = {
  id: string;
  weekIndex: number;
  type: string;
  subject: string | null;
  body: string | null;
  /** cadence_step state: not-started | finished | skipped | interrupted | responded | … */
  state: string;
};

export type TouchpointSwimlaneColumn = {
  /** the single step type this touch point drives */
  stepType: string;
  /** e.g. "Starts immediately", "When connection accepted" */
  activationLabel?: string;
  sends: TouchpointSwimlaneSend[];
};

export type TouchpointSwimlanesPureProps = {
  programName?: string;
  /** enrollment status; when 'responded'/'completed' the program has stopped */
  status?: string;
  columns: TouchpointSwimlaneColumn[];
  emptyState?: ReactNode;
  className?: string;
};

const STEP_TYPE_LABEL: Record<string, string> = {
  email: 'Email',
  message: 'LinkedIn message',
  inmail: 'InMail',
  connect: 'Connection',
  call: 'Call',
  visit: 'Profile visit',
  follow: 'Follow',
  likeRecentPosts: 'Like posts',
  voice_note: 'Voice note',
};

function stepTypeLabel(type: string): string {
  return STEP_TYPE_LABEL[type] ?? type;
}

function stripHtml(html: string | null): string {
  if (html == null) return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const STATUS_STYLE: Record<string, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  responded: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  completed: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

function StatusBadge({ status }: { status: string }): ReactNode {
  return (
    <span
      data-testid="swimlane-status"
      className={cn('rounded px-1.5 py-0.5 text-xs font-medium', STATUS_STYLE[status] ?? STATUS_STYLE.completed)}>
      {status}
    </span>
  );
}

function SendCell({ send }: { send: TouchpointSwimlaneSend }): ReactNode {
  const preview = stripHtml(send.subject) || stripHtml(send.body) || stepTypeLabel(send.type);
  return (
    <div
      data-testid="swimlane-send"
      className={cn(
        'flex flex-col gap-0.5 rounded border px-2 py-1',
        'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900',
        send.state === 'finished' && 'border-green-300 dark:border-green-700',
        send.state === 'skipped' && 'opacity-60',
      )}>
      <span className="truncate text-xs text-gray-800 dark:text-gray-200">{preview}</span>
      <span className="text-[10px] uppercase tracking-wide text-gray-400">{send.state}</span>
    </div>
  );
}

/**
 * Horizontal swimlanes for a contact's touch-point program: one column per
 * touch point (step type), weeks aligned as rows so a week reads across all
 * channels. This is the full-width builder/contact-view layout; the narrow
 * /todo panel falls back to a vertical list elsewhere.
 */
export function TouchpointSwimlanesPure({
  programName,
  status,
  columns,
  emptyState,
  className,
}: TouchpointSwimlanesPureProps): ReactNode {
  if (columns.length === 0) {
    return (
      <div data-testid="touchpoint-swimlanes-empty" className={cn('text-sm text-gray-500', className)}>
        {emptyState ?? 'No touch points attached.'}
      </div>
    );
  }

  const maxWeeks = columns.reduce((m, c) => Math.max(m, c.sends.length), 0);
  const gridTemplateColumns = `72px repeat(${columns.length}, minmax(160px, 1fr))`;

  return (
    <div data-testid="touchpoint-swimlanes" className={cn('flex flex-col gap-2', className)}>
      {(programName != null || status != null) && (
        <div className="flex items-center gap-2">
          {programName != null && (
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{programName}</span>
          )}
          {status != null && <StatusBadge status={status} />}
        </div>
      )}

      <div className="grid w-full gap-1" style={{ gridTemplateColumns }}>
        {/* header row: blank corner + one header per touch point */}
        <div />
        {columns.map((col) => (
          <div key={col.stepType} data-testid={`swimlane-col-${col.stepType}`} className="flex flex-col px-2 py-1">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{stepTypeLabel(col.stepType)}</span>
            {col.activationLabel != null && <span className="text-xs text-gray-500">{col.activationLabel}</span>}
          </div>
        ))}

        {/* one row per week, aligned across all columns */}
        {Array.from({ length: maxWeeks }).map((_, week) => (
          <Fragment key={`week-${week}`}>
            <div className="flex items-center justify-end pr-2 text-xs text-gray-500">Week {week + 1}</div>
            {columns.map((col) => {
              const send = col.sends[week];
              return (
                <div key={`${col.stepType}-${week}`} data-testid={`swimlane-cell-${col.stepType}-${week}`}>
                  {send == null && <div className="px-2 py-1 text-xs text-gray-300 dark:text-gray-600">—</div>}
                  {send != null && <SendCell send={send} />}
                </div>
              );
            })}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

export default TouchpointSwimlanesPure;
